import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { quotationSteps } from '@/mocks/quotationSteps';

const snakeOrder = [0, 1, 2, 5, 4, 3];

export default function QuotationProcess() {
  const { t } = useTranslation('home');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="w-full bg-white py-12 md:py-16 lg:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-700 mb-4">
              {t('quotation.title')}
            </h2>
            <p className="text-base md:text-lg text-ink-400 max-w-2xl mx-auto">
              {t('quotation.subtitle')}
            </p>
          </div>

          {/* Steps — Snake Layout */}
          <div ref={sectionRef} className="space-y-2">
            {/* Row 1: 1 → 2 → 3 */}
            <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-start gap-2 md:gap-4">
              {[0, 1, 2].map((visualIdx) => {
                const step = quotationSteps[snakeOrder[visualIdx]];
                return (
                  <>
                    <div
                      key={`cell-${visualIdx}`}
                      className={`flex flex-col items-center text-center transition-all duration-700 ${
                        isVisible
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-8'
                      }`}
                      style={{ transitionDelay: `${visualIdx * 150}ms` }}
                    >
                      {/* Number Badge + Icon */}
                      <div className="relative">
                        <div className="absolute -top-4 -left-4 md:-top-5 md:-left-5 text-3xl md:text-4xl font-black text-ink-700 z-10 leading-none">
                          {visualIdx + 1}
                        </div>
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-[1.5px] border-ink-700 flex items-center justify-center bg-white">
                          <i className={`${step.icon} text-3xl md:text-4xl text-ink-700 ${step.icon.includes('truck') ? 'scale-x-[-1]' : ''}`} />
                        </div>
                      </div>
                      {/* Text */}
                      <div className="mt-5 md:mt-6">
                        <h3 className="text-base md:text-lg font-bold text-ink-700 leading-snug">
                          {t(step.titleKey)}
                        </h3>
                        <p className="text-sm md:text-base text-ink-400 mt-1.5 max-w-[180px] md:max-w-[220px] leading-relaxed">
                          {t(step.descKey)}
                        </p>
                      </div>
                    </div>
                    {/* Arrow between circles (except last) */}
                    {visualIdx < 2 && (
                      <div
                        key={`arrow-${visualIdx}`}
                        className={`hidden md:flex items-center justify-center pt-8 transition-all duration-700 ${
                          isVisible ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{ transitionDelay: `${visualIdx * 150 + 100}ms` }}
                      >
                        <i className="ri-arrow-right-line text-4xl lg:text-5xl text-ink-700" />
                      </div>
                    )}
                  </>
                );
              })}
            </div>

            {/* Down arrow: 3 → 4 */}
            <div className="flex justify-end pr-[8%] md:pr-[10%] lg:pr-[12%]">
              <div
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                }`}
                style={{ transitionDelay: '450ms' }}
              >
                <i className="ri-arrow-down-line text-4xl md:text-5xl text-ink-700" />
              </div>
            </div>

            {/* Row 2: 6 ← 5 ← 4 */}
            <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-start gap-2 md:gap-4">
              {[3, 4, 5].map((visualIdx, arrIdx) => {
                const step = quotationSteps[snakeOrder[visualIdx]];
                return (
                  <>
                    <div
                      key={`cell-${visualIdx}`}
                      className={`flex flex-col items-center text-center transition-all duration-700 ${
                        isVisible
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-8'
                      }`}
                      style={{ transitionDelay: `${visualIdx * 150}ms` }}
                    >
                      {/* Number Badge + Icon */}
                      <div className="relative">
                        <div className="absolute -top-4 -left-4 md:-top-5 md:-left-5 text-3xl md:text-4xl font-black text-ink-700 z-10 leading-none">
                          {6 - arrIdx}
                        </div>
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-[1.5px] border-ink-700 flex items-center justify-center bg-white">
                          <i className={`${step.icon} text-3xl md:text-4xl text-ink-700 ${step.icon.includes('truck') ? 'scale-x-[-1]' : ''}`} />
                        </div>
                      </div>
                      {/* Text */}
                      <div className="mt-5 md:mt-6">
                        <h3 className="text-base md:text-lg font-bold text-ink-700 leading-snug">
                          {t(step.titleKey)}
                        </h3>
                        <p className="text-sm md:text-base text-ink-400 mt-1.5 max-w-[180px] md:max-w-[220px] leading-relaxed">
                          {t(step.descKey)}
                        </p>
                      </div>
                    </div>
                    {/* Arrow between circles (except last) */}
                    {visualIdx < 5 && (
                      <div
                        key={`arrow-${visualIdx}`}
                        className={`hidden md:flex items-center justify-center pt-8 transition-all duration-700 ${
                          isVisible ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{ transitionDelay: `${visualIdx * 150 + 100}ms` }}
                      >
                        <i className="ri-arrow-left-line text-4xl lg:text-5xl text-ink-700" />
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          {/* 
          <div className="text-center mt-16 md:mt-20">
            <a
              href="/quotation"
              className="inline-flex items-center gap-2.5 px-8 py-4 bg-ink-700 text-white font-semibold rounded-xl hover:bg-ink-600 transition-colors whitespace-nowrap text-base"
            >
              <i className="ri-file-list-3-line text-lg" />
              {t('nav.getQuote')}
              <i className="ri-arrow-right-line text-lg" />
            </a>
          </div>
          */}
        </div>
      </div>
    </section>
  );
}